import {
  Backdrop,
  Box,
  CircularProgress,
  FormHelperText,
  InputAdornment,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as constants from "../../../constants";
import CustomAutocomplete from "../../../shared/AutoComplete";
import ContainedButton from "../../../shared/Button/CustomContainedButton";
import OutlinedButton from "../../../shared/Button/customOutlinedButton";
import CustomTextField from "../../../shared/CustomTextFeild";
import MultiEmailInput from "../../../shared/MultiEmailInput";
import { RootState } from "../../../store/store";
import {
  SectionTitle,
  StyledOneChildContainer,
  StyledParentContainer,
  StyledTwoChildContainer,
} from "../StyledComponents/Container";
import { getClientDetailsByClientName } from "../Utils/ClientSettingsTable";
import CommonUtilities from "../Utils/CommonUtilities";
import { notifyUser } from "../Utils/notifyUser";
import {
  getAllParticipants,
  getAndPostIntrestRateForAllEntity,
  getAndPostIntrestRateForCheckings,
  getAndPostIntrestRateForCreditLine,
  getAndPostIntrestRateForSavings,
  getEntityNamesByPId,
  getIntrestRateForAllEntity,
  getIntrestRateForCheckings,
  getIntrestRateForCreditLine,
  getIntrestRateForSavings,
  getMonthsList,
} from "../services/MonthlyCalculations/monthlyServices";
import { getSystemSettings } from "../services/Settings/SystemSettingsService";
const formatAmount = (
  amount: number | null | undefined,
  locale: string = "en-US",
  currency: string = "USD"
): string => {
  const formatter = new Intl.NumberFormat(locale, {
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  return formatter.format(amount ?? 0);
};
const MonthlyCalculations = () => {
  const contactInformation = useSelector(
    (state: RootState) => state.participantAdminProfile
  );
  const [userRole] = useState<string>(
    contactInformation.participantAdminProfile
  );
  const userInfo = useSelector((state: RootState) => state.auth);
  const { t } = useTranslation();
  const translate = t as (key: string) => string;
  const [participantArray, setParticipantArray] = useState([]);
  const [loanOption, setLoanOption] = useState(false);
  const [selectedParticipant, setSelectedParticipant] = useState<any>(null);
  const [selectedaccountType, setSelectedaccountType] = useState(null);
  const [entityAccountNameArray, setEntityAccountNameArray] = useState([{ id: 0, name: "Select" }]);
  const [datesArray, setDatesArray] = useState([]);
  const [selectedAccountName, setSelectedAccountName] = useState({ id: 0, name: "Select" });
  const [selectedMonth, setSelectedMonth] = useState<any | null>(null);
  const [emails, setEmails] = useState<string[]>([]);
  const ClientDetails = getClientDetailsByClientName(userInfo?.clientVo?.name);
  const [generatedStatementInterest, setGeneratedStatementInterest] = useState<
    any | null
  >(null);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [errors, setErrors] = useState({
    participant: false,
    accountType: false,
    accountName: false,
    month: false,
    emails: "",
  });

  const isValidEmail = (email: string) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email.trim());
  };
  const entityTypes = ClientDetails?.allowedPaymentTypes === constants.ALLOWED_PAYMENT_TYPES_NON_DNL
    ? CommonUtilities.entityTypesOptions.filter(type => type !== "Checking")
    : CommonUtilities.entityTypesOptions;

  const validateEmails = (emails: string[]) => {
    if (!emails || emails.length === 0) return translate("pleaseEnterAValidEmail");
    for (const email of emails) {
      if (!isValidEmail(email)) {
        return translate("invalidEmail");
      }
    }
    return "";
  };

  const handleEmailsChange = (emails: string[]) => {
    setEmails(emails);
    const errorMessage = validateEmails(emails);
    setErrors((prev) => ({ ...prev, emails: errorMessage }));
  };

  async function getAllParticipantsList() {
    setIsLoading(true);
    try {
      const ParticipantVo = {
        participantName: "",
        isActive: "Y",
        clientId: userInfo.clientId,
      };
      const responseList = await getAllParticipants(ParticipantVo);
      if (responseList.status === constants.STATUS_CODES.SUCCESS) {
        const participants = responseList.data.data.map((p: any) => ({
          participantId: p.participantId,
          participantName: p.participantName,
        })) || [];
        participants.unshift({
          participantId: 0,
          participantName: "All",
        });
        setParticipantArray(participants);
        if(participants.length > 0 && userRole !== constants.ADMIN_VIEW){
          const defaultParticipant = participants.find((p: any) => p.participantId === userInfo.defaultParticipantId);
          setSelectedParticipant(defaultParticipant || participants[0]);
        }
      } else {
        setParticipantArray([]);
      }
    } catch (error) {
      console.error("Error fetching Participants List:", error);
    } finally {
      setIsLoading(false);
    }
  }
  function handleBack() {
    navigate("/monthlyListing");
  }

  async function getLoanOptionSetting() {
    try {
      const response = await getSystemSettings(constants.ATTRIBUTE_NAME.SETTING_MONTHLY, userInfo.clientId);
      if (response.data != null && response.status === constants.STATUS_CODES.SUCCESS) {
        const extractedValue = response.data.data[0].value;
        if (extractedValue === constants.ATTRIBUTE_NAME.SETTING_MONTH_END) {
          setLoanOption(true);
        } else {
          setLoanOption(false);
        }
      }
    } catch (error) {
      console.error(error);
    }
  }

  useEffect(() => {
    getLoanOptionSetting();
  }, [userInfo.clientId]);

  async function getAccountNameListByPid(
    pId: number,
    entityName: string,
    clientId: number
  ) {
    setIsLoading(true);
    try {
      const response = await getEntityNamesByPId(pId, entityName, clientId);
      if (response.status === constants.STATUS_CODES.SUCCESS) {
        if (selectedaccountType === "Checking") {
          setEntityAccountNameArray([{ id: 0, name: "Checking" }]);
        } else {
          setEntityAccountNameArray(response.data.data);
        }
      }
    } catch (error) {
      console.error("Error handling fund source:", error);
    } finally {
      setIsLoading(false);
    }
  }
const isLastDayOfMonth = (date = new Date()) => {
  const tomorrow = new Date(date);
  tomorrow.setDate(date.getDate() + 1);

  return tomorrow.getDate() === 1 ? 1 : 0;
};

  async function getMonthsListByCurentMonth(
    numberOfMonths: number,
    CurrentMOnth: number,
    clientId: number
  ) {
    setIsLoading(true);
    try {
      const response = await getMonthsList(
        numberOfMonths,
        CurrentMOnth,
        clientId
      );
      if (response.status === constants.STATUS_CODES.SUCCESS) {
        const data = response.data.data.map((dates: any) => ({
          date: new Date(dates).toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
          }),
        }));
        setDatesArray(data);
      }
    } catch (error) {
      console.error("Error handling fund source:", error);
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    getAllParticipantsList();
    getMonthsListByCurentMonth(6, isLastDayOfMonth(), userInfo.clientId);
    setEntityAccountNameArray([{ id: 0, name: "Select" }]);
  }, []);

  useEffect(() => {
    if (selectedaccountType) {
      if (selectedaccountType === "Checking") {
        setEntityAccountNameArray([{ id: 0, name: "Checking" }]);
      } else {
        const Pid =
          userRole === constants.ADMIN_VIEW
            ? selectedParticipant?.participantId
            : userInfo?.defaultParticipantId;
        getAccountNameListByPid(Pid, selectedaccountType, userInfo.clientId);
      }
    } else {
      setEntityAccountNameArray([{ id: 0, name: "Select" }]);
    }
  }, [selectedaccountType]);

  const validateField = (field: string, value: any) => {
    switch (field) {
      case "participant":
        return userRole === constants.ADMIN_VIEW ? (value ? false : true) : false;
      case "accountType":
        return value ? false : true;
      case "accountName":
        return value ? false : true;
      case "month":
        return value ? false : true;
      default:
        return false;
    }
  };

  const handleFieldChange = (field: string, value: any) => {
    switch (field) {
      case "participant":
        setSelectedParticipant(value);
        setSelectedaccountType(null);
        setEntityAccountNameArray([{ id: 0, name: "Select" }]);
        setSelectedAccountName({ id: 0, name: "Select" });
        setSelectedMonth(null);
        break;
      case "accountType":
        setSelectedaccountType(value);
        setEntityAccountNameArray([{ id: 0, name: "Select" }]);
        setSelectedAccountName({ id: 0, name: "Select" });
        setSelectedMonth(null);
        break;
      case "accountName":
        setSelectedAccountName(value);
        break;
      case "month":
        setSelectedMonth(value);
        break;
      default:
        break;
    }

    // Validate the field
    setErrors((prevErrors) => ({
      ...prevErrors,
      [field]: validateField(field, value),
    }));
  };

  const validateForm = () => {
    const newErrors = {
      participant: validateField("participant", selectedParticipant),
      accountType: validateField("accountType", selectedaccountType),
      accountName: selectedaccountType !== "All" ? validateField("accountName", selectedAccountName) : false,
      month: validateField("month", selectedMonth),
      emails: selectedaccountType === "All" ? validateEmails(emails) : "",
    };

    setErrors(newErrors);
    return Object.values(newErrors).every((error) => !error);
  };

  const handleGenerate = async () => {
    setFormSubmitted(true);
    if (validateForm()) {
      if (selectedaccountType === "All") {
        setIsLoading(true);
        const allEntityVo = {
          participantId:
            userRole === constants.ADMIN_VIEW
              ? selectedParticipant.participantId
              : userInfo.defaultParticipantId,
          clientId: userInfo.clientId,
          entityCategory: selectedaccountType,
          startPeriod: (() => {
            const d = new Date(selectedMonth.date);
            const s = new Date(d.getFullYear(), d.getMonth(), 1, 0, 0, 0, 0);
            return s.getTime();
          })(),
          endPeriod: (() => {
            const d = new Date(selectedMonth.date);
            const e = new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59, 999);
            return e.getTime();
          })(),
          emails: emails,
        };
        try {
          const response = await getIntrestRateForAllEntity(allEntityVo);
          if (response.status === constants.STATUS_CODES.SUCCESS) {
            notifyUser(
              response.data.message,
              "success",
              translate("selectCriteria")
            );
            setGeneratedStatementInterest(response.data.data);
          } else {
            notifyUser(
              response.response.data.message,
              "error",
              translate("selectCriteria")
            );
          }
        } catch (error) {
          console.error("Error handling fund source:", error);
        } finally {
          setIsLoading(false);
        }
      }
      if (selectedaccountType === "Savings") {
        setIsLoading(true);
        const id = selectedAccountName.id;
        const Pid =
          userRole === constants.ADMIN_VIEW
            ? selectedParticipant.participantId
            : userInfo.defaultParticipantId;
        const Cid = userInfo.clientId;
        const dateString = selectedMonth.date;
        const timestamp = new Date(dateString).getTime();
        try {
          const response = await getIntrestRateForSavings(
            id,
            Pid,
            timestamp,
            Cid
          );
          if (response.status === constants.STATUS_CODES.SUCCESS) {
            notifyUser(
              response.data.message,
              "success",
              translate("selectCriteria")
            );
            setGeneratedStatementInterest(response.data.data);
          } else {
            notifyUser(
              response.response.data.message,
              "error",
              translate("selectCriteria")
            );
          }
        } catch (error) {
          console.error("Error handling fund source:", error);
        } finally {
          setIsLoading(false);
        }
      }

      if (selectedaccountType === "Checking") {
        setIsLoading(true);
        const Pid =
          userRole === constants.ADMIN_VIEW
            ? selectedParticipant.participantId
            : userInfo.defaultParticipantId;
        const Cid = userInfo.clientId;
        const dateString = selectedMonth.date;
        const timestamp = new Date(dateString).getTime();
        try {
          const response = await getIntrestRateForCheckings(
            Pid,
            timestamp,
            Cid
          );
          if (response.status === constants.STATUS_CODES.SUCCESS) {
            notifyUser(
              response.data.message,
              "success",
              translate("selectCriteria")
            );
            setGeneratedStatementInterest(response.data.data);
          } else {
            notifyUser(
              response.response.data.message,
              "error",
              translate("selectCriteria")
            );
          }
        } catch (error) {
          console.error("Error handling fund source:", error);
        } finally {
          setIsLoading(false);
        }
      }

      if (selectedaccountType === "CreditLine") {
        setIsLoading(true);
        const id = selectedAccountName.id;
        const Pid =
          userRole === constants.ADMIN_VIEW
            ? selectedParticipant.participantId
            : userInfo.defaultParticipantId;
        const Cid = userInfo.clientId;
        const inputDate = selectedMonth.date;
        const formattedDate = new Date(inputDate).toISOString().split("T")[0];
        try {
          const response = await getIntrestRateForCreditLine(
            id,
            Pid,
            formattedDate,
            Cid
          );
          if (response.status === constants.STATUS_CODES.SUCCESS) {
            notifyUser(
              response.data.message,
              "success",
              translate("selectCriteria")
            );
            setGeneratedStatementInterest(response.data.data);
          } else {
            notifyUser(
              response.response.data.message,
              "error",
              translate("selectCriteria")
            );
          }
        } catch (error) {
          console.error("Error handling fund source:", error);
        } finally {
          setIsLoading(false);
        }
      }
    }
  };
  const handleGenerateAndPost = async () => {
    setFormSubmitted(true);
    if (validateForm()) {
      if (selectedaccountType === "Savings") {
        setIsLoading(true);
        const id = selectedAccountName.id;
        const Pid =
          userRole === constants.ADMIN_VIEW
            ? selectedParticipant.participantId
            : userInfo.defaultParticipantId;
        const Cid = userInfo.clientId;
        const dateString = selectedMonth.date;
        const timestamp = new Date(dateString).getTime();
        try {
          const response = await getAndPostIntrestRateForSavings(
            id,
            Pid,
            timestamp,
            Cid
          );
          if (response.status === constants.STATUS_CODES.SUCCESS) {
            notifyUser(
              response.data.message,
              "success",
              translate("selectCriteria")
            );
            setGeneratedStatementInterest(response.data.data);
          } else {
            notifyUser(
              response.response.data.message,
              "error",
              translate("selectCriteria")
            );
          }
        } catch (error) {
          console.error("Error handling fund source:", error);
        } finally {
          setIsLoading(false);
        }
      }
      if (selectedaccountType === "All") {
        setIsLoading(true);

        const allEntityVo = {
          participantId:
            userRole === constants.ADMIN_VIEW
              ? selectedParticipant.participantId.toString()
              : userInfo.defaultParticipantId.toString(),
          clientId: userInfo.clientId,
          entityCategory: selectedaccountType,
          startPeriod: (() => {
            const d = new Date(selectedMonth.date);
            return new Date(d.getFullYear(), d.getMonth(), 1, 0, 0, 0, 0).getTime();
          })(),
          endPeriod: (() => {
            const d = new Date(selectedMonth.date);
            return new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59, 999).getTime();
          })(),
          emails: emails,
        };
        try {
          const response = await getAndPostIntrestRateForAllEntity(allEntityVo);
          if (response.status === constants.STATUS_CODES.SUCCESS) {
            notifyUser(
              response.data.message,
              "success",
              translate("selectCriteria")
            );
            setGeneratedStatementInterest(response.data.data);
          } else {
            notifyUser(
              response.response.data.message,
              "error",
              translate("selectCriteria")
            );
          }
        } catch (error) {
          console.error("Error handling fund source:", error);
        } finally {
          setIsLoading(false);
        }
      }
      if (selectedaccountType === "Checking") {
        setIsLoading(true);
        const Pid =
          userRole === constants.ADMIN_VIEW
            ? selectedParticipant.participantId
            : userInfo.defaultParticipantId;
        const dateString = selectedMonth.date;
        const timestamp = new Date(dateString).getTime();
        try {
          const response = await getAndPostIntrestRateForCheckings(
            Pid,
            timestamp
          );
          if (response.status === constants.STATUS_CODES.SUCCESS) {
            notifyUser(
              response.data.message,
              "success",
              translate("selectCriteria")
            );
            setGeneratedStatementInterest(response.data.data);
          } else {
            notifyUser(
              response.response.data.message,
              "error",
              translate("selectCriteria")
            );
          }
        } catch (error) {
          console.error("Error handling fund source:", error);
        } finally {
          setIsLoading(false);
        }
      }

      if (selectedaccountType === "CreditLine") {
        setIsLoading(true);
        const id = selectedAccountName.id;
        const Pid =
          userRole === constants.ADMIN_VIEW
            ? selectedParticipant.participantId
            : userInfo.defaultParticipantId;
        const Cid = userInfo.clientId;
        const inputDate = selectedMonth.date;
        const date = new Date(inputDate);
        const startOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
        const formattedStartDate = startOfMonth.toISOString().split("T")[0];
        const formattedEndDate = new Date(inputDate)
          .toISOString()
          .split("T")[0];
        try {
          const response = await getAndPostIntrestRateForCreditLine(
            id,
            Pid,
            formattedStartDate,
            formattedEndDate,
            Cid
          );
          if (response.status === constants.STATUS_CODES.SUCCESS) {
            notifyUser(
              response.data.message,
              "success",
              translate("selectCriteria")
            );
            setGeneratedStatementInterest(response.data.data);
          } else {
            notifyUser(
              response.response.data.message,
              "error",
              translate("selectCriteria")
            );
          }
        } catch (error) {
          console.error("Error handling fund source:", error);
        } finally {
          setIsLoading(false);
        }
      }
    }
  };

  return (
    <StyledParentContainer>
      <StyledOneChildContainer>
        <StyledTwoChildContainer sx={{ border: "none" }}>
          <SectionTitle>{translate("selectCriteria")}</SectionTitle>
          <StyledTwoChildContainer>
            <Box sx={{ width: "100%", padding: "40px" }}>
              <Grid container spacing={2} direction={"column"}>
                  <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                    <CustomAutocomplete
                      labelText={translate("participantName")}
                      required
                      options={participantArray}
                      value={selectedParticipant}
                      onChange={(event, newValue) =>
                        handleFieldChange("participant", newValue)
                      }
                      readOnly={userRole !== constants.ADMIN_VIEW}
                      getOptionLabel={(option) => option.participantName}
                      isOptionEqualToValue={(option, value) =>
                        option.participantId === value?.participantId
                      }
                      placeholder={translate("participantName")}
                      error={formSubmitted && Boolean(errors.participant)}
                    />
                    {formSubmitted && errors.participant && (
                      <FormHelperText error>
                        {translate("pleaseSelectaParticipant")}
                      </FormHelperText>
                    )}
                  </Grid>
                <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                  <CustomAutocomplete
                    labelText={translate("accountType")}
                    required
                    options={entityTypes.filter(option => option !== "Loan")}
                    value={selectedaccountType}
                    onChange={(event, newValue) =>
                      handleFieldChange("accountType", newValue)
                    }
                    placeholder={translate("accountType")}
                    error={formSubmitted && Boolean(errors.accountType)}
                  />
                  {formSubmitted && errors.accountType && (
                    <FormHelperText error>
                      {translate("pleaseSelectAccountType")}
                    </FormHelperText>
                  )}
                </Grid>
                {selectedaccountType !== "All" && <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                  <CustomAutocomplete
                    labelText={translate("accountName")}
                    required
                    options={entityAccountNameArray || []}
                    value={selectedAccountName}
                    onChange={(event, newValue) =>
                      handleFieldChange("accountName", newValue)
                    }
                    getOptionLabel={(option) =>
                      option.id === 0 || option.id === 1 ? option.name : `${option.id}-${option.name}`
                    }
                    placeholder={translate("accountName")}
                    error={formSubmitted && Boolean(errors.accountName)}
                  />
                  {formSubmitted && errors.accountName && (
                    <FormHelperText error>
                      {translate("pleaseSelectAccountName")}
                    </FormHelperText>
                  )}
                </Grid>}
                <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                  <CustomAutocomplete
                    labelText={translate("month")}
                    required
                    options={datesArray}
                    value={selectedMonth}
                    onChange={(event, newValue) =>
                      handleFieldChange("month", newValue)
                    }
                    getOptionLabel={(option) => option.date}
                    placeholder={translate("month")}
                    error={formSubmitted && errors.month}
                  />
                  {formSubmitted && errors.month && (
                    <FormHelperText error>
                      {translate("pleaseSelectaMonth")}
                    </FormHelperText>
                  )}
                </Grid>
                {selectedaccountType !== "All" && <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                  <CustomTextField
                    readOnly
                    labelText={translate("interestForTheStatementPeriodIs")}
                    value={
                      generatedStatementInterest &&
                      formatAmount(generatedStatementInterest)
                    }
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                    }}
                  />
                </Grid>}
                {selectedaccountType === "All" && <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                  <MultiEmailInput
                    required
                    labelText={translate("emailId")}
                    value={emails}
                    onChange={handleEmailsChange}
                    error={Boolean(errors.emails)}
                    helperText={errors.emails}
                  />
                </Grid>}
              </Grid>

              <div
                className="search-fields-container"
                style={{ marginTop: "10px" }}
              >
                <div className="left-fields">
                  <ContainedButton onClick={handleBack}>
                    {translate("back")}
                  </ContainedButton>
                  <ContainedButton onClick={handleGenerate}>
                    {translate("generate")}
                  </ContainedButton>
                  <OutlinedButton onClick={handleGenerateAndPost}>
                    {`${translate("generate")} + ${translate(ClientDetails?.financialSystemToPostInterests || "post")}`}
                  </OutlinedButton>
                </div>
              </div>
            </Box>
            <Backdrop
              sx={(theme) => ({
                color: "#fff",
                zIndex: theme.zIndex.drawer + 1,
                display: "flex",
                flexDirection: "column",
              })}
              open={isLoading}
            >
              <CircularProgress color="inherit" />
              <Typography color="white">Please wait...</Typography>
            </Backdrop>
          </StyledTwoChildContainer>
        </StyledTwoChildContainer>
      </StyledOneChildContainer>
    </StyledParentContainer>
  );
};

export default MonthlyCalculations;
